import type { StaffListItem } from '@/definitions/staff'

import { staffDisplayName } from './staffLabels'

const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

/**
 * Whether a typed staff id could be one at all.
 *
 * Shape only — whether the staff member exists, and whether they have a clinician
 * worth sharing, is api-service's to answer. Catching the shape here keeps a
 * mistyped id from being sent as a real request.
 */
export const isStaffIdShaped = (value: string): boolean => UUID_PATTERN.test(value.trim())

export interface StaffErxConfirmCopy {
  title: string
  effect: string
  /** Present when an existing clinician is being shared rather than a new one built. */
  linkNote?: string
  confirmLabel: string
  /** Whether the question is posed as taking something away. */
  isDestructive: boolean
}

export interface StaffErxConfirmInput {
  staff: StaffListItem
  enabling: boolean
  /** The clinician to share, when the operator supplied one. */
  linkStaffId?: string
}

/**
 * The question asked before a staff member's E-Prescribe is switched.
 *
 * Asked in both directions, unlike reactivating a login: each direction reaches
 * Dosespot, and the dialog is where the one thing the switch cannot show gets said —
 * that a shared clinician was asked for.
 *
 * Deliberately says what the request does rather than what Dosespot will end up
 * doing with it: which of the two is authoritative for a staff member's prescribing
 * is an open question, so this claims only the part that is settled.
 *
 * Built as data rather than as markup so the wording and the conditions are testable.
 */
export const staffErxConfirmCopy = ({
  staff,
  enabling,
  linkStaffId,
}: StaffErxConfirmInput): StaffErxConfirmCopy => {
  const name = staffDisplayName(staff)

  if (!enabling) {
    return {
      title: `Turn off E-Prescribe for ${name}?`,
      effect: `E-Prescribe is switched off for ${name} and their Dosespot clinician is deactivated.`,
      confirmLabel: 'Turn off E-Prescribe',
      isDestructive: true,
    }
  }

  return {
    title: `Turn on E-Prescribe for ${name}?`,
    effect: `${name} gets a Dosespot clinician under this organization's clinic.`,
    linkNote:
      linkStaffId !== undefined && linkStaffId !== ''
        ? 'This organization’s clinic is added to the clinician that staff member ' +
          `${linkStaffId} already has, so the two practices share one Dosespot ` +
          'identity instead of a second one being created.'
        : undefined,
    confirmLabel: 'Turn on E-Prescribe',
    isDestructive: false,
  }
}

/** The line confirming a change that went through, named so the toast is about a person. */
export const staffErxSuccessMessage = (
  staff: StaffListItem,
  isEnabled: boolean
): string =>
  isEnabled
    ? `E-Prescribe is on for ${staffDisplayName(staff)}.`
    : `E-Prescribe is off for ${staffDisplayName(staff)}.`
